import React, { useState } from 'react';
import { Company, PolicyType, PolicyRevision } from '../../types';
import { useCompanyTimeline } from '../../hooks/useCompanyTimeline';
import { TimelineRail } from './TimelineRail';
import { VersionCompareSelector } from './VersionCompareSelector';
import { DiffViewer } from '../diff/DiffViewer';
import { DocumentReader } from '../reader/DocumentReader';
import { cleanPolicyTitle } from '../../utils/policyUtils';
import { ArrowLeft, FileText, ShieldCheck, BookOpen, Clock, GitCompare, ChevronDown } from 'lucide-react';

interface TimelineViewProps {
  companyId: string;
  policyType: PolicyType;
  initialBaseRevId?: string | null;
  initialTargetRevId?: string | null;
  initialViewMode?: 'timeline' | 'reader';
  companies: Company[];
  onSelectCompany: (id: string) => void;
  onSelectPolicy: (type: PolicyType) => void;
  onBackToCatalog: () => void;
  onJobCreated?: (job: any) => void;
}

export const TimelineView: React.FC<TimelineViewProps> = ({
  companyId,
  policyType,
  initialBaseRevId,
  initialTargetRevId,
  initialViewMode = 'timeline',
  companies,
  onSelectCompany,
  onSelectPolicy,
  onBackToCatalog,
  onJobCreated,
}) => {
  const { revisions, loading, error, refreshTimeline } = useCompanyTimeline(companyId, policyType);

  const [activeDiffView, setActiveDiffView] = useState<boolean>(Boolean(initialBaseRevId && initialTargetRevId));
  const [baseRevId, setBaseRevId] = useState<string | null>(initialBaseRevId || null);
  const [targetRevId, setTargetRevId] = useState<string | null>(initialTargetRevId || null);
  const [viewMode, setViewMode] = useState<'timeline' | 'reader'>(initialViewMode);
  const [selectedRevisionForReading, setSelectedRevisionForReading] = useState<PolicyRevision | null>(null);

  const currentCompany = companies.find((c) => c.id === companyId) || {
    id: companyId,
    name: companyId.toUpperCase(),
    domain: `${companyId}.com`,
    category: 'AI / Tech',
  };

  const handleLaunchDiff = (baseId: string, targetId: string) => {
    setBaseRevId(baseId);
    setTargetRevId(targetId);
    setActiveDiffView(true);
    setViewMode('timeline');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleReadRevision = (rev: PolicyRevision) => {
    setSelectedRevisionForReading(rev);
    setViewMode('reader');
    setActiveDiffView(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentDocRevision = selectedRevisionForReading || revisions[0] || null;

  return (
    <div className="space-y-6">
      {/* Top Header & Breadcrumb */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-slate-800 p-5 rounded-xl border border-slate-200 dark:border-slate-700/80 shadow-2xs">
        <div className="flex items-center gap-4">
          <button
            onClick={onBackToCatalog}
            className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 transition-colors"
            title="Back to Catalog"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                {currentCompany.name}
              </h1>
              <span className="text-xs px-2.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700/80 text-slate-600 dark:text-slate-300 font-medium">
                {currentCompany.category}
              </span>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Historical policy change tracking, document reading & multi-version diffs
            </p>
          </div>
        </div>

        {/* Company & Policy Switcher */}
        <div className="flex items-center gap-2">
          <select
            value={companyId}
            onChange={(e) => {
              onSelectCompany(e.target.value);
              setSelectedRevisionForReading(null);
            }}
            className="bg-slate-50 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-xs font-semibold text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer"
          >
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Policy Tabs & View Mode Toggles */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-200 dark:border-slate-700">
        {/* Dynamic Policy Type Switcher */}
        <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto pb-2 sm:pb-0">
          {(() => {
            const trackedPolicies = (currentCompany as any).trackedPolicies || (currentCompany as any).tracked_policies || {};
            const policyEntries = Object.keys(trackedPolicies).length > 0
              ? Object.entries(trackedPolicies).map(([id, cfg]: [string, any]) => ({
                  id,
                  name: cleanPolicyTitle(cfg?.name, id),
                }))
              : [
                  { id: 'terms_of_service', name: 'Terms of Service' },
                  { id: 'privacy_policy', name: 'Privacy Policy' },
                ];

            // Sort to prioritize core agreements
            const sorted = [...policyEntries].sort((a, b) => {
              if (a.id === 'terms_of_service' || a.id === 'privacy_policy') return -1;
              if (b.id === 'terms_of_service' || b.id === 'privacy_policy') return 1;
              return 0;
            });

            const primary = sorted.slice(0, 3);
            const secondary = sorted.slice(3);
            const isSecondarySelected = secondary.some((s) => s.id === policyType);

            return (
              <>
                {primary.map((p) => {
                  const isSelected = policyType === p.id;
                  const isPrivacy = p.id.includes('privacy');
                  return (
                    <button
                      key={p.id}
                      onClick={() => {
                        onSelectPolicy(p.id);
                        setActiveDiffView(false);
                        setSelectedRevisionForReading(null);
                      }}
                      className={`flex items-center gap-1.5 pb-3 px-2 border-b-2 font-semibold text-xs sm:text-sm whitespace-nowrap transition-colors ${
                        isSelected
                          ? isPrivacy
                            ? 'border-teal-600 text-teal-600 dark:text-teal-400 dark:border-teal-400'
                            : 'border-indigo-600 text-indigo-600 dark:text-indigo-400 dark:border-indigo-400'
                          : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400'
                      }`}
                    >
                      {isPrivacy ? <ShieldCheck className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                      <span>{p.name}</span>
                    </button>
                  );
                })}

                {secondary.length > 0 && (
                  <div className="relative pb-2 sm:pb-0">
                    <select
                      value={isSecondarySelected ? policyType : ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          onSelectPolicy(e.target.value);
                          setActiveDiffView(false);
                          setSelectedRevisionForReading(null);
                        }
                      }}
                      className={`text-xs font-semibold rounded-lg px-2.5 py-1.5 border transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                        isSecondarySelected
                          ? 'bg-indigo-50 dark:bg-indigo-950/80 border-indigo-500 text-indigo-700 dark:text-indigo-300'
                          : 'bg-slate-100 dark:bg-slate-700/80 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300'
                      }`}
                    >
                      <option value="" disabled>
                        More Sub-Agreements ({secondary.length})...
                      </option>
                      {secondary.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {/* View Mode Toggle: Timeline vs Document */}
        {!activeDiffView && (
          <div className="flex items-center gap-1.5 pb-2">
            <button
              onClick={() => setViewMode('timeline')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                viewMode === 'timeline'
                  ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 shadow-2xs'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
            >
              <Clock className="w-3.5 h-3.5" />
              Timeline History
            </button>

            <button
              onClick={() => {
                setViewMode('reader');
                setSelectedRevisionForReading(revisions[0] || null);
              }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                viewMode === 'reader'
                  ? 'bg-indigo-600 text-white shadow-2xs'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
            >
              <BookOpen className="w-3.5 h-3.5" />
              Read Current Document
            </button>
          </div>
        )}
      </div>

      {/* Arbitrary Version Comparison Selector Bar */}
      {revisions.length >= 2 && !activeDiffView && viewMode === 'timeline' && (
        <VersionCompareSelector
          revisions={revisions}
          onCompare={handleLaunchDiff}
          currentBaseId={baseRevId}
          currentTargetId={targetRevId}
        />
      )}

      {/* Main Content Render: DiffViewer vs DocumentReader vs TimelineRail */}
      {activeDiffView && baseRevId && targetRevId ? (
        <DiffViewer
          companyId={companyId}
          policyType={policyType}
          baseRevisionId={baseRevId}
          targetRevisionId={targetRevId}
          revisions={revisions}
          company={currentCompany as Company}
          onReadDocument={handleReadRevision}
          onClose={() => setActiveDiffView(false)}
        />
      ) : viewMode === 'reader' && currentDocRevision ? (
        <DocumentReader
          revision={currentDocRevision}
          company={currentCompany as Company}
          policyType={policyType}
          onBack={() => setViewMode('timeline')}
          onCompareDiff={revisions.length >= 2 ? () => handleLaunchDiff(revisions[1].id, revisions[0].id) : undefined}
        />
      ) : (
        <TimelineRail
          revisions={revisions}
          loading={loading}
          error={error}
          onViewDiff={handleLaunchDiff}
          onReadDocument={handleReadRevision}
        />
      )}
    </div>
  );
};
